import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, FloppyDisk, FilePdf, CheckCircle, Robot, ArrowsOut, ArrowsIn, ClockCounterClockwise } from "@phosphor-icons/react";
import RichTextEditor from "@/components/RichTextEditor";
import AIAssistantPanel from "@/components/AIAssistantPanel";
import SignatarioDownloadModal from "@/components/SignatarioDownloadModal";

// Template inicial se documento nunca foi editado — usa texto simples do despacho
const textToHTML = (raw) => {
  if (!raw) return "<p></p>";
  return raw
    .split(/\n\n+/)
    .map((p) => `<p>${p.replace(/\n/g, "<br>").replace(/</g, "&lt;")}</p>`)
    .join("");
};

export default function DespachoEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [despacho, setDespacho] = useState(null);
  const [html, setHtml] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [selectedText, setSelectedText] = useState("");
  const [approvedForPdf, setApprovedForPdf] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const htmlRef = useRef(html);
  htmlRef.current = html;

  // Carrega rascunho
  useEffect(() => {
    api.get(`/despachos/${id}/rascunho`).then((r) => {
      const d = r.data;
      setDespacho(d);
      const initial = d.rich_text_html || textToHTML(d.texto);
      setHtml(initial);
      setSavedAt(d.draft_updated_at);
    }).catch((err) => {
      toast.error(err?.response?.data?.detail || "Erro ao carregar rascunho");
    });
  }, [id]);

  // Autosave
  const salvar = useCallback(async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/despachos/${id}/rascunho`, { rich_text_html: htmlRef.current });
      setSavedAt(data.saved_at);
      setDirty(false);
    } catch (err) {
      toast.error("Erro ao salvar rascunho");
    } finally { setSaving(false); }
  }, [id, dirty, saving]);

  useEffect(() => {
    if (!dirty) return;
    const t = setTimeout(salvar, 3000);  // 3s debounce
    return () => clearTimeout(t);
  }, [html, dirty, salvar]);

  // Captura seleção de texto
  useEffect(() => {
    const handleSelection = () => {
      const sel = window.getSelection();
      const text = sel && sel.toString().trim();
      setSelectedText(text || "");
    };
    document.addEventListener("selectionchange", handleSelection);
    return () => document.removeEventListener("selectionchange", handleSelection);
  }, []);

  const onEditorChange = (newHtml) => {
    setHtml(newHtml);
    setDirty(true);
    setApprovedForPdf(false);  // qualquer edição reset a aprovação
  };

  // Aplica sugestão da IA (substitui todo doc)
  const onApplyReplace = (newHtml) => {
    setHtml(newHtml);
    setDirty(true);
    setApprovedForPdf(false);
  };

  // Aplica ao trecho selecionado — busca no HTML e substitui pela string plana
  const onApplySelectionReplace = (newHtml) => {
    if (!selectedText) { onApplyReplace(newHtml); return; }
    // Simplificação: substitui a primeira ocorrência da seleção no HTML
    // (a resposta da IA vem em HTML, então inline substitui)
    const plain = newHtml.replace(/^<p>|<\/p>$/g, "");
    const updated = html.replace(selectedText, plain);
    setHtml(updated);
    setDirty(true);
    setApprovedForPdf(false);
  };

  const aprovar = async () => {
    await salvar();
    setApprovedForPdf(true);
    toast.success("Documento aprovado. Agora selecione o assinante para gerar o PDF.");
    setShowPdfModal(true);
  };

  if (!despacho) return <div className="text-center py-10 text-[#94A3B8]">Carregando editor…</div>;

  const savedInfo = savedAt ? `Salvo às ${new Date(savedAt).toLocaleTimeString("pt-BR")}` : "Rascunho ainda não salvo";

  return (
    <div className={fullscreen ? "fixed inset-0 z-[80] bg-[#0F172A] p-4 overflow-hidden flex flex-col" : "space-y-4"}>
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          {!fullscreen && (
            <button onClick={() => navigate("/despachos")} data-testid="editor-back"
                    className="p-2 rounded-md border border-[#334155] hover:border-[#94A3B8] text-[#94A3B8]">
              <ArrowLeft size={16} />
            </button>
          )}
          <div>
            <div className="uppercase-label flex items-center gap-1.5">
              <Robot size={12} weight="duotone" className="text-[#8B5CF6]" />
              Editor Inteligente
            </div>
            <div className="font-heading text-2xl mt-0.5">Despacho {despacho.numero}</div>
            <div className="text-xs text-[#94A3B8] mt-0.5">
              Contrato {despacho.contract_numero} · {despacho.municipio_nome} · {despacho.documento_ref}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-xs text-[#94A3B8] flex items-center gap-1.5" data-testid="autosave-indicator">
            {saving ? (<><span className="w-2 h-2 rounded-full bg-[#F59E0B] animate-pulse" /> Salvando…</>) : dirty ? (
              <>⚠ Alterações pendentes</>
            ) : (<><span className="w-2 h-2 rounded-full bg-[#10B981]" /> {savedInfo}</>)}
          </div>
          <button data-testid="editor-fullscreen" onClick={() => setFullscreen((f) => !f)}
                  title={fullscreen ? "Sair do modo tela cheia" : "Modo tela cheia"}
                  className="p-2 rounded-md border border-[#334155] hover:border-[#94A3B8] text-[#94A3B8]">
            {fullscreen ? <ArrowsIn size={16} /> : <ArrowsOut size={16} />}
          </button>
          <button data-testid="editor-save" onClick={salvar} disabled={!dirty || saving}
                  className="px-4 py-2 rounded-md border border-[#334155] hover:border-[#F59E0B] text-[#E9EEF5] text-sm font-medium disabled:opacity-40 flex items-center gap-2">
            <FloppyDisk size={14} weight="duotone" /> Salvar rascunho
          </button>
          <button data-testid="editor-approve" onClick={aprovar}
                  className="px-4 py-2 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] font-medium text-sm flex items-center gap-2">
            <CheckCircle size={14} weight="bold" /> Aprovar e gerar PDF
          </button>
        </div>
      </div>

      {/* Split view: editor 70% / IA 30% */}
      <div className={`grid grid-cols-1 lg:grid-cols-10 gap-4 ${fullscreen ? "flex-1 overflow-hidden" : ""}`} style={{ minHeight: fullscreen ? "auto" : "72vh" }}>
        <div className={`lg:col-span-7 ${fullscreen ? "h-full overflow-hidden" : ""}`}>
          <RichTextEditor content={html} onChange={onEditorChange} testid="doc-editor" />
        </div>
        <div className={`lg:col-span-3 ${fullscreen ? "h-full overflow-hidden" : ""}`}>
          <AIAssistantPanel
            despachoId={id}
            getDocumentoAtual={() => htmlRef.current}
            onApplyReplace={onApplyReplace}
            onApplySelectionReplace={onApplySelectionReplace}
            selectedText={selectedText}
          />
        </div>
      </div>

      {showPdfModal && (
        <SignatarioDownloadModal
          municipioId={despacho.municipio_id}
          urlBase={`/despachos/${id}/pdf`}
          filename={`despacho_${(despacho.numero || "").replace(/[^\w.-]/g, "_")}.pdf`}
          title="Despacho aprovado → PDF final"
          subtitle={`${despacho.numero} · ${despacho.municipio_nome}`}
          onClose={() => setShowPdfModal(false)}
          onSuccess={() => navigate("/despachos")}
        />
      )}
    </div>
  );
}
